import EventIcon from '@mui/icons-material/Event';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { inventoryAlertSummaryApi } from 'api/inventory';
import { handleError } from 'store/thunks/errorHandlerThunks';

import { useInventoryAlert } from '../context/InventoryAlertContext';

const ALL_FACILITIES = 'ALL';

const useCountUp = (target, duration = 600) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined || target === 0) {
      setCount(target ?? 0);
      return undefined;
    }
    let current = 0;
    const step = Math.max(1, Math.ceil(target / (duration / 16)));
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setCount(current);
      if (current >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
};

const SECTION_SX = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1.5,
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': { bgcolor: 'action.hover' },
};

const STRIP_COLORS = ['#e65100', '#ff9800', 'info.main'];

const LOW_STOCK_TOOLTIP =
  'Drugs currently below their reorder point. Critical = below 25% of reorder qty. Low = 25–100%. Prevents prescription fill failures.';
const EXPIRING_SOON_TOOLTIP =
  'Lots expiring within 30 days. Sorted by soonest expiry. Helps prevent dispensing expired medication and reduces financial waste.';
const LOW_SUPPLY_TOOLTIP =
  'Flags drugs projected to run out within 14 days based on 14-day average dispense rate. Lead time comparison (order now vs delivery ETA) available in Phase 2.';

const METRIC_INFO = [
  { label: 'Low Stock', color: '#e65100', text: LOW_STOCK_TOOLTIP },
  { label: 'Expiring Soon', color: '#ff9800', text: EXPIRING_SOON_TOOLTIP },
  { label: '14 Day Supply', color: '#1976d2', text: LOW_SUPPLY_TOOLTIP },
];

const CARD_PAPER_SX = (color) => ({
  display: 'flex',
  minWidth: 290,
  maxWidth: 380,
  borderLeft: '4px solid',
  borderLeftColor: color,
  borderRadius: 2,
  overflow: 'hidden',
});

const CardSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    {STRIP_COLORS.map((color) => (
      <Paper key={color} variant='outlined' sx={CARD_PAPER_SX(color)}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Skeleton variant='circular' width={20} height={20} sx={{ mt: 0.25 }} />
            <Box>
              <Skeleton variant='text' width={90} height={20} />
              <Skeleton variant='text' width={130} height={16} />
            </Box>
          </Box>
          <Skeleton variant='text' width={32} height={30} />
        </Box>
      </Paper>
    ))}
  </Box>
);

const LowStockSection = ({ data, onClick }) => {
  const count = useCountUp(data.total);
  return (
    <Box sx={SECTION_SX} onClick={onClick} role='button' tabIndex={0}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <WarningAmberIcon sx={{ color: '#e65100', fontSize: 20, mt: 0.2 }} />
        <Box>
          <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            Low Stock
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
            <Chip
              label={`${data.criticalCount ?? 0} critical`}
              size='small'
              sx={{
                bgcolor: '#fdecea',
                color: '#c62828',
                fontWeight: 600,
                fontSize: 10,
                height: 18,
              }}
            />
            <Chip
              label={`${data.lowStockCount ?? 0} low`}
              size='small'
              sx={{
                bgcolor: '#fff3e0',
                color: '#e65100',
                fontWeight: 600,
                fontSize: 10,
                height: 18,
              }}
            />
          </Box>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1,
          color: data.total > 0 ? '#e65100' : 'text.primary',
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};
LowStockSection.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.number,
    criticalCount: PropTypes.number,
    lowStockCount: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const ExpiringSoonSection = ({ data, onClick }) => {
  const count = useCountUp(data.total);
  return (
    <Box sx={SECTION_SX} onClick={onClick} role='button' tabIndex={0}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <EventIcon sx={{ color: '#ff9800', fontSize: 20, mt: 0.2 }} />
        <Box>
          <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            Expiring Soon
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            lots expiring within {data.withinDays ?? 30} days
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1,
          color: data.total > 0 ? '#ff9800' : 'text.primary',
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};
ExpiringSoonSection.propTypes = {
  data: PropTypes.shape({ total: PropTypes.number, withinDays: PropTypes.number }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const LowDaysOfSupplySection = ({ data, onClick }) => {
  const count = useCountUp(data.total);
  return (
    <Box sx={SECTION_SX} onClick={onClick} role='button' tabIndex={0}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TrendingDownIcon sx={{ color: 'info.main', fontSize: 20, mt: 0.2 }} />
        <Box>
          <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            {data.thresholdDays ?? 14} Day Supply
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {data.total ?? 0} drugs below {data.thresholdDays ?? 14}-day threshold
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1,
          color: data.total > 0 ? 'info.main' : 'text.primary',
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};
LowDaysOfSupplySection.propTypes = {
  data: PropTypes.shape({ total: PropTypes.number, thresholdDays: PropTypes.number }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const MetricCards = ({ facilityId }) => {
  const reduxDispatch = useDispatch();
  const { state, dispatch } = useInventoryAlert();
  const { summary, summaryRefreshKey } = state;
  const prevFacilityRef = useRef(facilityId);
  const [infoAnchor, setInfoAnchor] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (prevFacilityRef.current !== facilityId) {
      dispatch({ type: 'CLEAR_ALL_PAGES' });
      prevFacilityRef.current = facilityId;
    }

    const filter =
      facilityId && facilityId !== ALL_FACILITIES
        ? { fulfillingPharmacyId: facilityId }
        : undefined;

    dispatch({ type: 'SUMMARY_LOADING', loading: true });
    inventoryAlertSummaryApi({ filter })
      .then((data) => {
        if (!cancelled) dispatch({ type: 'SUMMARY_LOADED', payload: data });
      })
      .catch((err) => {
        if (!cancelled) {
          dispatch({ type: 'SUMMARY_LOADING', loading: false });
          reduxDispatch(handleError(err));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, dispatch, reduxDispatch, summaryRefreshKey]);

  const openDialog = (dialogType) => {
    const facilityIdForFilter = facilityId && facilityId !== ALL_FACILITIES ? facilityId : null;
    dispatch({ type: 'OPEN_DIALOG', dialogType, facilityId: facilityIdForFilter });
  };

  if (summary.loading) return <CardSkeleton />;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Paper variant='outlined' sx={CARD_PAPER_SX('#e65100')}>
        <LowStockSection data={summary.lowStock} onClick={() => openDialog('lowStock')} />
      </Paper>
      <Paper variant='outlined' sx={CARD_PAPER_SX('#ff9800')}>
        <ExpiringSoonSection
          data={summary.expiringSoon}
          onClick={() => openDialog('expiringSoon')}
        />
      </Paper>
      <Paper variant='outlined' sx={CARD_PAPER_SX('info.main')}>
        <LowDaysOfSupplySection
          data={summary.lowDaysOfSupply}
          onClick={() => openDialog('lowDaysOfSupply')}
        />
      </Paper>

      <IconButton
        size='small'
        onClick={(e) => setInfoAnchor(e.currentTarget)}
        sx={{ color: 'text.disabled', flexShrink: 0, alignSelf: 'flex-start' }}
      >
        <InfoOutlinedIcon fontSize='small' />
      </IconButton>

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          {METRIC_INFO.map(({ label, color, text }, i) => (
            <Box key={label}>
              {i > 0 && <Divider sx={{ my: 1.5 }} />}
              <Typography variant='body2' sx={{ fontWeight: 700, color, mb: 0.5 }}>
                {label}
              </Typography>
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', lineHeight: 1.6, display: 'block' }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

MetricCards.propTypes = {
  facilityId: PropTypes.string.isRequired,
};

export default MetricCards;
